import { useAuth } from './useAuth';

/**
 * useCan
 * Lightweight client-side permission helper consuming permissions from AuthProvider.
 * Structure: permissions = {
 *   [section]: { level: 'none' | 'read' | 'write', actions?: { [action]: true } }
 * }
 * Semantics:
 * - Section write implicitly satisfies read.
 * - If actions map absent, section.level==='write' implies all actions; read implies no actions.
 */
export function useCan() {
  const { permissions } = useAuth();

  function getSection(section) {
    if (!permissions) return undefined;
    // Try direct, then naive plural/singular fallbacks for transition period.
    const direct = permissions[section];
    if (direct) return direct;
    if (!section.endsWith('s') && permissions[section + 's']) return permissions[section + 's'];
    if (section.endsWith('s')) {
      const singular = section.slice(0, -1);
      if (permissions[singular]) return permissions[singular];
    }
    return undefined;
  }

  function resolveLevel(entry) {
    return entry.level || entry.accessLevel || 'none';
  }

  function hasSectionLevel(section, requiredLevel) {
    const entry = getSection(section);
    if (!entry) return false;
    const current = resolveLevel(entry);
    if (requiredLevel === 'read') return current === 'read' || current === 'write';
    if (requiredLevel === 'write') return current === 'write';
    return false;
  }

  function hasAction(section, action) {
    const entry = getSection(section);
    if (!entry) return false;
    // If an actions map exists, it is authoritative: only explicitly true grants are allowed.
    if (entry.actions) {
      if (Object.prototype.hasOwnProperty.call(entry.actions, action)) {
        return !!entry.actions[action]; // explicit true or false
      }
      // Action not listed: treat as denied when map is present (explicit model)
      return false;
    }
    // No actions map defined: fall back to section-level semantics (write implies all actions)
    return resolveLevel(entry) === 'write';
  }

  function evaluate(item) {
    if (!item) return false;
    if (item.section && item.level) return hasSectionLevel(item.section, item.level);
    if (item.section && item.action) return hasAction(item.section, item.action);
    return false;
  }

  function all(requirements = []) {
    return requirements.every(r => evaluate(r));
  }

  function any(requirements = []) {
    return requirements.some(r => evaluate(r));
  }

  function explain(requirement) {
    if (!requirement) return { allowed: false, reason: 'No requirement provided' };
    const { section, action, level } = requirement;
    const entry = getSection(section);
    if (!entry) return { allowed: false, reason: `No permissions for section '${section}'` };
    if (level) {
      const allowed = hasSectionLevel(section, level);
      return allowed
        ? { allowed: true }
        : { allowed: false, reason: `Requires ${level} but has ${entry.level}` };
    }
    if (action) {
      const allowed = hasAction(section, action);
      return allowed
        ? { allowed: true }
        : { allowed: false, reason: `Action '${action}' not granted` };
    }
    return { allowed: false, reason: 'Invalid requirement shape' };
  }

  return {
    // Original API
    section: (section, requirement) => {
      if (!requirement) return false;
      if (requirement.level) return hasSectionLevel(section, requirement.level);
      if (requirement.action) return hasAction(section, requirement.action);
      return false;
    },
    action: (section, action) => hasAction(section, action),
    // Enhanced helpers
    any, // any([{ section:'invoice', level:'read' }, { section:'invoice', action:'transfer' }])
    all, // all([...])
    explain // returns { allowed, reason? }
  };
}
